import {B2BOrderDetailParam, OrderVerifyStatus} from '@buncha/B2B/types';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import ImageWithQuantityPill from '@buncha/common/components/ImageWithQuantityPill';
import {Modules} from '@buncha/utils/types';
import {OrderItems} from '@buncha/batchDetails/types';
import {Touchable} from '@buncha/components/core/touchable';
import {formatCurrency} from '@buncha/utils/common';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface ReturnOrderItemProps {
  item: OrderItems;
  onDrop?: (item: OrderItems) => void;
}

const ReturnOrderItem: React.FC<ReturnOrderItemProps> = props => {
  const {item, onDrop} = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {body} = useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const routeParams = useRoute()?.params as B2BOrderDetailParam | undefined;
  const status = routeParams?.status;
  const dropItemEligibleStatus = [
    OrderVerifyStatus.pending,
    OrderVerifyStatus.validationDone,
    OrderVerifyStatus.validationFailed,
    OrderVerifyStatus.validationInProgress,
  ];

  const getLeftComponent = useCallback(() => {
    return (
      <Touchable onPress={() => onDrop?.(item)}>
        <H6Text
          style={[
            styles.dropText,
            {
              fontFamily: muliFont[600].normal,
              color: theme?.background.red[100],
            },
          ]}>
          Drop
        </H6Text>
      </Touchable>
    );
  }, [item, muliFont, onDrop, theme?.background.red]);

  return (
    <HBox style={styles.container}>
      <ImageWithQuantityPill
        imageLink={item.itemImageUrl}
        quantity={item.quantity}
      />
      <Box style={styles.subContainer}>
        <H5Text
          numberOfLines={2}
          style={[
            styles.textStyle,
            styles.titleStyle,
            {
              fontFamily: muliFont[700].normal,
              color: theme?.text.blue[300],
            },
          ]}>
          {item.name}
        </H5Text>
        <HBox>
          <H6Text
            style={[
              styles.textStyle,
              styles.subText,
              {
                fontFamily: muliFont[700].normal,
                color: theme?.text.blue[300],
              },
            ]}>
            {formatCurrency(item.price)}
          </H6Text>
          <If
            condition={dropItemEligibleStatus.includes(
              status ?? OrderVerifyStatus.updationFailed,
            )}>
            {getLeftComponent()}
          </If>
        </HBox>
        <If condition={status === OrderVerifyStatus.updationFailed}>
          <CustomText
            style={[
              styles.textStyle,
              {
                fontFamily: muliFont[600].normal,
                color: theme?.text.gray[100],
              },
            ]}>
            {body?.returnBack}
          </CustomText>
        </If>
      </Box>
    </HBox>
  );
};

export default React.memo(ReturnOrderItem);
